/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';

import { environment } from 'environments/environment';
import { Constants } from 'app/shared/models/constants.model';
import { InviteDTO } from 'app/shared/models/invite.model';
import { HttpHelpersService } from './http-helpers.service';

@Injectable({ providedIn: 'root' })
export class InviteService {
  constructor(private http: HttpClient) {}

  checkInvite(id: number) {
    return this.http.get(`${environment.apiHostname}/v1/invitations/${id}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  updateInvite(payload: InviteDTO) {
    return this.http.put(`${environment.apiHostname}/v1/invitations/${payload.id}`, payload).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  setInviteToUser(id: number, toUserId: number) {
    if (!id) {
      return of(false);
    }

    return this.http
      .put(`${environment.apiHostname}/v1/invitations/${id}`, {
        toUser: { id: toUserId },
      })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  updateUserInvites(email: string, toUserId: number, newEmail: string) {
    if (!toUserId) {
      return of(false);
    }

    return this.http
      .put(`${environment.apiHostname}/v1/invitations/of-email/${email}/to-user/${toUserId}/${newEmail}`, {})
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  getExistingInvite(userId: number, inviteeEmail: string, purchaseId: number) {
    return this.http
      .get(`${environment.apiHostname}/v1/invitations/get-existing-invite/${userId}/${purchaseId}/${inviteeEmail}`)
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  addInvite(payload: InviteDTO) {
    return this.http.post(`${environment.apiHostname}/v1/invitations/`, payload).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  deleteInvite(inviteId: number) {
    return this.http.delete(`${environment.apiHostname}/v1/invitations/${inviteId}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  deleteInvitesByPurchaseIdAndUserId(purchaseId: number, userId: number) {
    return this.http
      .delete(`${environment.apiHostname}/v1/invitations/by-purchase-user-id/${purchaseId}/${userId}`)
      .pipe(
        map((response: any) => {
          return response.data;
        })
      );
  }

  sendInviteEmail(email: string, inviteId: number) {
    return HttpHelpersService.handleGetResponse(
      this.http.post(`${environment.apiHostname}/v1/emails/${inviteId}/email-invitee/${email}`, {})
    );
  }

  checkInvitesByUserId(userId: number) {
    return this.http.get(`${environment.apiHostname}/v1/invitations/by-user/id/${userId}`).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  getInvitesForEmail(email: string) {
    return HttpHelpersService.handleGetResponse(
      this.http.get(`${environment.apiHostname}/v1/invitations/for-user/email/${email}`)
    );
  }

  setupInvites(selectedPlan, userData) {
    const emptyInvitees = [];
    let includesTeamPlans = false;
    const athletesForEvent = {};

    const isOneEventTpPresent = selectedPlan.options.find(
      (element) => element.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP
    );

    includesTeamPlans = !!isOneEventTpPresent;

    if (!(userData.purchases && userData.purchases.length === 0)) {
      selectedPlan.options.forEach((option, index) => {
        if (
          option.type !== Constants.PURCHASE_TYPE.EXPEDITED_DELIVERY &&
          option.type !== Constants.PURCHASE_TYPE.BREAKDOWN
        ) {
          const key = option.event ? option.event.id : option.type;
          athletesForEvent[key] = athletesForEvent[key] ? athletesForEvent[key] + 1 : 1;
        }

        const listKey = option.event ? option.event.id : option.type;

        /* create invites for all products purchased by a PARENT */
        if (
          athletesForEvent[listKey] > 1 ||
          (athletesForEvent[listKey] === 1 && userData.type === Constants.USER_TYPE.PARENT)
        ) {
          /* exclude TP purchased by non-parents */
          if (
            (option.type !== Constants.PURCHASE_TYPE.ONE_EVENT_TP ||
              (option.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP && userData.type === Constants.USER_TYPE.PARENT)) &&
            option.type !== Constants.PURCHASE_TYPE.EXPEDITED_DELIVERY &&
            option.type !== Constants.PURCHASE_TYPE.BREAKDOWN &&
            option.type !== Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME &&
            option.type !== Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME_TP
          ) {
            const inviteeData = {
              inviteeFullName: option.invitee ? option.invitee.inviteeFullName : '',
              inviteeEmail: option.invitee ? option.invitee.inviteeEmail : '',
              user: { id: userData.id },
              purchase: option.purchase,
              inviteeSportId: option.sportId,
              optionIndex: index,
            };
            emptyInvitees.push(inviteeData);
          }
        }
        // }
      });
    }

    return {
      emptyInvitees,
      includesTeamPlans,
    };
  }

  getInvitedAthletes(userId: number) {
    return this.checkInvitesByUserId(userId).pipe(
      map((invites) => {
        const myAthletes = invites.reduce((acc, invite) => {
          // if user created for invite
          // and user is not already saved
          if (
            invite.toUser &&
            acc.every((existingInvite) => existingInvite && existingInvite.id !== invite.toUser.id)
          ) {
            acc.push(invite.toUser);
          }

          return acc;
        }, []);

        myAthletes.sort((a, b) => (a > b ? -1 : 1));
        return myAthletes;
      })
    );
  }

  getInvitedUsers(userId: number) {
    return this.http.get(`${environment.apiHostname}/v1/invitations/by-user/id/${userId}`).pipe(
      map((response: any) => {
        return response.data.reduce((acc, invite) => {
          if (invite && invite.toUser) {
            acc.push({
              ...invite.toUser,
              purchaseId: invite.purchase.id,
              invitedByUserId: invite.user.id,
              isTeamInvite: invite.isTeamInvite,
            });
          }
          return acc;
        }, []);
      })
    );
  }
}
